import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, GraduationCap, Shield, Clock, Users, Monitor, Mail, CalendarIcon, CreditCard, Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, getDay, isAfter, startOfDay } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { z } from 'zod';
import { WaitlistDialog } from '@/components/training/WaitlistDialog';
import { generateWebinarPDF } from '@/lib/generateWebinarPDF';

type Step = 'description' | 'account' | 'confirm' | 'slot' | 'payment';

const guestSchema = z.object({
  fullName: z.string().trim().min(2).max(100),
  email: z.string().trim().email().max(255),
});

const WEBINAR_PRICE = '199,99 € HT';

const webinarData = {
  fr: {
    'ia-generative-juridique': {
      title: 'Introduction à l\'IA générative juridique',
      subtitle: 'Découvrez les fondamentaux de l\'IA générative appliquée au droit : ChatGPT, Claude, Gemini et les outils spécialisés.',
      description: [
        'Comprendre les principes de fonctionnement des LLM (Large Language Models)',
        'Découvrir les cas d\'usage concrets en cabinet d\'avocat et direction juridique',
        'Identifier les risques et limites de l\'IA générative pour le droit',
        'Maîtriser les bonnes pratiques de prompt engineering juridique',
        'Comparer les principaux outils : ChatGPT, Claude, Gemini, Harvey, Doctrine',
      ],
      format: 'Webinaire · 2h · Visioconférence en direct',
      audience: 'Avocats · Juristes · Étudiants en droit',
      date: 'Mars 2026',
    },
    'prompt-engineering-juridique': {
      title: 'Prompt engineering juridique avancé',
      subtitle: 'Apprenez à formuler des prompts efficaces pour obtenir des résultats juridiques précis et fiables.',
      description: [
        'Structurer des prompts complexes pour l\'analyse contractuelle',
        'Techniques de chain-of-thought pour le raisonnement juridique',
        'Créer des templates de prompts réutilisables pour votre cabinet',
        'Optimiser la recherche jurisprudentielle assistée par IA',
        'Vérifier et valider les réponses de l\'IA (anti-hallucination)',
      ],
      format: 'Webinaire · 2h · Visioconférence en direct',
      audience: 'Avocats · Juristes · Collaborateurs',
      date: 'Mars 2026',
    },
    'securite-deontologie-ia': {
      title: 'Sécurité & déontologie de l\'IA',
      subtitle: 'Maîtrisez les enjeux de sécurité des données et de conformité déontologique dans l\'usage de l\'IA.',
      description: [
        'Protéger le secret professionnel dans l\'utilisation des LLM',
        'Conformité RGPD et traitement des données personnelles par l\'IA',
        'Obligations déontologiques de l\'avocat face aux outils IA',
        'Sécuriser les flux de données : chiffrement, anonymisation, cloisonnement',
        'Mettre en place une charte d\'usage IA au sein du cabinet',
      ],
      format: 'Webinaire · 2h · Visioconférence en direct',
      audience: 'Avocats · Juristes · Directions juridiques',
      date: 'Avril 2026',
    },
  },
  en: {
    'intro-legal-generative-ai': {
      title: 'Introduction to Legal Generative AI',
      subtitle: 'Discover the fundamentals of generative AI applied to law: ChatGPT, Claude, Gemini and specialized tools.',
      description: [
        'Understand how LLMs (Large Language Models) work',
        'Discover concrete use cases in law firms and legal departments',
        'Identify risks and limitations of generative AI for law',
        'Master legal prompt engineering best practices',
        'Compare main tools: ChatGPT, Claude, Gemini, Harvey, Doctrine',
      ],
      format: 'Webinar · 2h · Live video conference',
      audience: 'Lawyers · Counsel · Law students',
      date: 'March 2026',
    },
    'advanced-legal-prompt-engineering': {
      title: 'Advanced Legal Prompt Engineering',
      subtitle: 'Learn to craft effective prompts for precise and reliable legal results.',
      description: [
        'Structure complex prompts for contract analysis',
        'Chain-of-thought techniques for legal reasoning',
        'Create reusable prompt templates for your firm',
        'Optimize AI-assisted case law research',
        'Verify and validate AI responses (anti-hallucination)',
      ],
      format: 'Webinar · 2h · Live video conference',
      audience: 'Lawyers · Counsel · Associates',
      date: 'March 2026',
    },
    'ai-security-ethics': {
      title: 'AI Security & Ethics',
      subtitle: 'Master data security and ethical compliance challenges in AI usage.',
      description: [
        'Protect professional secrecy when using LLMs',
        'GDPR compliance and personal data processing by AI',
        'Lawyer\'s ethical obligations regarding AI tools',
        'Secure data flows: encryption, anonymization, compartmentalization',
        'Implement an AI usage charter within the firm',
      ],
      format: 'Webinar · 2h · Live video conference',
      audience: 'Lawyers · Counsel · Legal departments',
      date: 'April 2026',
    },
  },
};

// Slug mapping for FR/EN
const slugMap: Record<string, string> = {
  'ia-generative-juridique': 'intro-legal-generative-ai',
  'prompt-engineering-juridique': 'advanced-legal-prompt-engineering',
  'securite-deontologie-ia': 'ai-security-ethics',
  'intro-legal-generative-ai': 'ia-generative-juridique',
  'advanced-legal-prompt-engineering': 'prompt-engineering-juridique',
  'ai-security-ethics': 'securite-deontologie-ia',
};

const CALENDAR_START = new Date(2026, 4, 1); // May 1, 2026

// Available: Tuesdays (2) and Thursdays (4) from May 2026
const isAvailableDay = (date: Date) => {
  const day = getDay(date);
  return (day === 2 || day === 4) && isAfter(date, startOfDay(CALENDAR_START));
};

export const WebinarBookingPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isFr = language === 'fr';

  const frCatalog = webinarData.fr;
  const enCatalog = webinarData.en;
  const training = slug
    ? isFr
      ? frCatalog[slug as keyof typeof frCatalog]
      : enCatalog[slug as keyof typeof enCatalog]
    : null;

  const [step, setStep] = useState<Step>('description');
  const [acceptCGU, setAcceptCGU] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestErrors, setGuestErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [showWaitlist, setShowWaitlist] = useState(false);

  useEffect(() => {
    if (step === 'payment') setShowWaitlist(true);
  }, [step]);

  const allSteps: { key: Step; label: string }[] = [
    { key: 'description', label: isFr ? 'Programme' : 'Program' },
    { key: 'account', label: isFr ? 'Compte' : 'Account' },
    { key: 'confirm', label: isFr ? 'Confirmation' : 'Confirm' },
    { key: 'slot', label: isFr ? 'Date' : 'Date' },
    { key: 'payment', label: isFr ? 'Paiement' : 'Payment' },
  ];
  const currentStepIndex = allSteps.findIndex(s => s.key === step);

  if (!training) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground mb-4">{isFr ? 'Formation introuvable.' : 'Training not found.'}</p>
            <Button asChild><Link to="/">{isFr ? 'Retour à l\'accueil' : 'Back to home'}</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDescriptionContinue = () => {
    if (user) {
      setStep('confirm');
    } else {
      setStep('account');
    }
  };

  const handleGuestContinue = () => {
    setGuestErrors({});
    try {
      guestSchema.parse({ fullName: guestName, email: guestEmail });
      setStep('confirm');
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        err.errors.forEach(e => { if (e.path[0]) errors[e.path[0] as string] = e.message; });
        setGuestErrors(errors);
      }
    }
  };

  const handleConfirmContinue = () => {
    if (!acceptCGU) {
      toast({
        variant: 'destructive',
        title: isFr ? 'CGU requises' : 'Terms required',
        description: isFr ? 'Veuillez accepter les conditions générales.' : 'Please accept the terms of service.',
      });
      return;
    }
    setStep('slot');
  };

  const handleSlotContinue = () => {
    if (!selectedDate) {
      toast({
        variant: 'destructive',
        title: isFr ? 'Date requise' : 'Date required',
        description: isFr ? 'Veuillez sélectionner une date.' : 'Please select a date.',
      });
      return;
    }
    setStep('payment');
  };

  const handlePayment = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.rpc('insert_enrollment', {
        p_training_slug: slug || '',
        p_training_title: training.title,
        p_modality: 'distanciel',
        p_total_price: WEBINAR_PRICE,
        p_guest_name: user ? null : guestName || null,
        p_guest_email: user ? null : guestEmail || null,
        p_selected_webinars: selectedDate ? JSON.stringify({ date: format(selectedDate, 'yyyy-MM-dd'), time: '12:15-14:15' }) : null,
      });

      if (error) {
        toast({ variant: 'destructive', title: isFr ? 'Erreur' : 'Error', description: error.message });
        setIsSubmitting(false);
        return;
      }

      toast({
        title: isFr ? 'Inscription confirmée !' : 'Registration confirmed!',
        description: isFr ? 'Vous recevrez un email de confirmation.' : 'You will receive a confirmation email.',
      });
      navigate(isFr ? '/formation-ia-avocat' : '/legal-ai-training-lawyers');
    } catch {
      toast({ variant: 'destructive', title: isFr ? 'Erreur' : 'Error', description: isFr ? 'Une erreur est survenue.' : 'An error occurred.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formattedDate = selectedDate
    ? isFr ? format(selectedDate, "EEEE d MMMM yyyy", { locale: frLocale }) : format(selectedDate, "EEEE, MMMM d, yyyy")
    : null;

  return (
    <div className="min-h-screen bg-secondary/30">
      <SEOHead
        title={`${training.title} | Gaius`}
        description={training.subtitle}
      />

      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {allSteps.map((s, i) => (
              <div key={s.key} className="flex items-center">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                  i < currentStepIndex ? 'bg-primary text-primary-foreground' :
                  i === currentStepIndex ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' :
                  'bg-muted text-muted-foreground'
                )}>
                  {i < currentStepIndex ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                {i < allSteps.length - 1 && (
                  <div className={cn("hidden sm:block w-12 lg:w-20 h-0.5 mx-1", i < currentStepIndex ? 'bg-primary' : 'bg-muted')} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            {allSteps.map(s => (
              <span key={s.key} className="text-xs text-muted-foreground hidden sm:block">{s.label}</span>
            ))}
          </div>
        </div>

        {/* Step 1: Description */}
        {step === 'description' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap className="w-6 h-6 text-primary" />
                <Badge className="bg-primary/10 text-primary border-primary/20">{isFr ? 'Webinaire' : 'Webinar'}</Badge>
                <Badge variant="outline" className="text-xs">{training.date}</Badge>
              </div>
              <CardTitle className="text-xl">{training.title}</CardTitle>
              <p className="text-sm text-muted-foreground">{training.subtitle}</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Info badges */}
              <div className="flex flex-wrap gap-3">
                <Badge variant="outline" className="text-sm px-3 py-1.5">
                  <Clock className="w-3.5 h-3.5 mr-1.5" />{isFr ? '2 heures' : '2 hours'}
                </Badge>
                <Badge variant="outline" className="text-sm px-3 py-1.5">
                  <Users className="w-3.5 h-3.5 mr-1.5" />{isFr ? '20 participants max' : '20 participants max'}
                </Badge>
                <Badge variant="outline" className="text-sm px-3 py-1.5">
                  <Monitor className="w-3.5 h-3.5 mr-1.5" />{isFr ? 'Visioconférence' : 'Video conference'}
                </Badge>
              </div>

              {/* Program details */}
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">{isFr ? 'Programme détaillé' : 'Detailed program'}</h3>
                {training.description.map((point, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{point}</span>
                  </div>
                ))}
              </div>

              {/* Audience */}
              <div className="p-4 rounded-xl border border-border bg-muted/30">
                <p className="text-sm"><span className="font-medium text-foreground">{isFr ? 'Public cible :' : 'Target audience:'}</span> <span className="text-muted-foreground">{training.audience}</span></p>
              </div>

              {/* Price */}
              <div className="p-4 rounded-xl border border-primary/20 bg-primary/5">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">{isFr ? 'Tarif par participant' : 'Price per participant'}</span>
                  <span className="text-2xl font-bold text-foreground">{WEBINAR_PRICE}</span>
                </div>
              </div>

              {/* Brochure download */}
              <Button variant="outline" className="w-full flex items-center gap-2" onClick={() => generateWebinarPDF(training, isFr ? 'fr' : 'en')}>
                <Download className="w-4 h-4" />
                {isFr ? 'Télécharger la brochure (PDF)' : 'Download brochure (PDF)'}
              </Button>

              <div className="flex justify-between pt-2">
                <Button variant="outline" asChild>
                  <Link to="/">
                    <ArrowLeft className="w-4 h-4 mr-2" />{isFr ? 'Retour' : 'Back'}
                  </Link>
                </Button>
                <Button onClick={handleDescriptionContinue}>
                  {isFr ? 'S\'inscrire' : 'Register'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Account */}
        {step === 'account' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl">{isFr ? 'Votre compte' : 'Your account'}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {isFr ? 'Créez un compte ou continuez en tant qu\'invité.' : 'Create an account or continue as guest.'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button asChild variant="outline" className="h-auto p-4 flex flex-col items-start gap-2">
                  <Link to={isFr ? '/connexion' : '/auth'}>
                    <Shield className="w-5 h-5 text-primary" />
                    <span className="font-semibold">{isFr ? 'Créer un compte' : 'Create account'}</span>
                    <span className="text-xs text-muted-foreground text-left">{isFr ? 'Accédez à vos formations' : 'Access your trainings'}</span>
                  </Link>
                </Button>
                <Button asChild variant="outline" className="h-auto p-4 flex flex-col items-start gap-2">
                  <Link to={isFr ? '/connexion' : '/auth'}>
                    <Mail className="w-5 h-5 text-primary" />
                    <span className="font-semibold">{isFr ? 'Se connecter' : 'Sign in'}</span>
                    <span className="text-xs text-muted-foreground text-left">{isFr ? 'Déjà un compte Gaius' : 'Already have a Gaius account'}</span>
                  </Link>
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">{isFr ? 'ou' : 'or'}</span></div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">{isFr ? 'Continuer en tant qu\'invité' : 'Continue as guest'}</h3>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="guest-name">{isFr ? 'Nom complet' : 'Full name'}</Label>
                    <Input id="guest-name" value={guestName} onChange={e => setGuestName(e.target.value)} placeholder={isFr ? 'Jean Dupont' : 'John Doe'} />
                    {guestErrors.fullName && <p className="text-xs text-destructive mt-1">{guestErrors.fullName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="guest-email">Email</Label>
                    <Input id="guest-email" type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="email@cabinet.com" />
                    {guestErrors.email && <p className="text-xs text-destructive mt-1">{guestErrors.email}</p>}
                  </div>
                </div>
                <Button onClick={handleGuestContinue} className="w-full">
                  {isFr ? 'Continuer en invité' : 'Continue as guest'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              <div className="flex justify-start pt-2">
                <Button variant="outline" onClick={() => setStep('description')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />{isFr ? 'Retour' : 'Back'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Confirmation inscription */}
        {step === 'confirm' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                {isFr ? 'Confirmer votre inscription' : 'Confirm your registration'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Recap */}
              <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                <h3 className="font-semibold text-foreground">{training.title}</h3>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>{training.format}</p>
                  <p>{training.audience}</p>
                </div>
                <div className="border-t border-border pt-3 flex justify-between items-center">
                  <span className="text-muted-foreground">{isFr ? 'Tarif' : 'Price'}</span>
                  <span className="text-xl font-bold text-foreground">{WEBINAR_PRICE}</span>
                </div>
              </div>

              {/* Participant info */}
              {(user || guestName) && (
                <div className="p-4 rounded-xl border border-border bg-muted/30">
                  <p className="text-sm font-medium text-foreground mb-1">{isFr ? 'Participant' : 'Participant'}</p>
                  <p className="text-sm text-muted-foreground">
                    {user ? user.email : `${guestName} — ${guestEmail}`}
                  </p>
                </div>
              )}

              {/* CGU opt-in */}
              <div className="p-4 rounded-xl border border-border bg-muted/30">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={acceptCGU} onCheckedChange={(v) => setAcceptCGU(v === true)} className="mt-0.5" />
                  <span className="text-sm text-muted-foreground">
                    {isFr ? (
                      <>J'accepte les <Link to="/conditions-utilisation" className="text-primary underline hover:no-underline">conditions générales d'utilisation</Link> et la <Link to="/confidentialite" className="text-primary underline hover:no-underline">politique de confidentialité</Link> de Gaius.</>
                    ) : (
                      <>I accept the <Link to="/terms-of-service" className="text-primary underline hover:no-underline">terms of service</Link> and <Link to="/privacy-policy" className="text-primary underline hover:no-underline">privacy policy</Link>.</>
                    )}
                  </span>
                </label>
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => user ? setStep('description') : setStep('account')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />{isFr ? 'Retour' : 'Back'}
                </Button>
                <Button onClick={handleConfirmContinue} disabled={!acceptCGU}>
                  {isFr ? 'Confirmer et choisir une date' : 'Confirm and choose a date'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Date selection */}
        {step === 'slot' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                {isFr ? 'Choisissez votre date' : 'Choose your date'}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {isFr
                  ? 'Sessions disponibles les mardis et jeudis, de 12h15 à 14h15.'
                  : 'Sessions available on Tuesdays and Thursdays, 12:15 PM – 2:15 PM.'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-wrap gap-3">
                <Badge variant="outline" className="text-sm px-3 py-1.5">
                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                  {isFr ? 'Mardi & Jeudi' : 'Tuesday & Thursday'}
                </Badge>
                <Badge variant="outline" className="text-sm px-3 py-1.5">
                  <Clock className="w-3.5 h-3.5 mr-1.5" />12:15 – 14:15
                </Badge>
              </div>

              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => !isAvailableDay(date)}
                  fromDate={CALENDAR_START}
                  toDate={new Date(2026, 11, 31)}
                  locale={isFr ? frLocale : undefined}
                  className={cn("p-3 pointer-events-auto rounded-xl border border-border")}
                  initialFocus
                />
              </div>

              {selectedDate && (
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground capitalize">{formattedDate}</p>
                      <p className="text-sm text-muted-foreground">12:15 – 14:15</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep('confirm')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />{isFr ? 'Retour' : 'Back'}
                </Button>
                <Button onClick={handleSlotContinue} disabled={!selectedDate}>
                  {isFr ? 'Continuer vers le paiement' : 'Continue to payment'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Payment */}
        {step === 'payment' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                {isFr ? 'Paiement' : 'Payment'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                <h3 className="font-semibold text-foreground">{training.title}</h3>
                {formattedDate && (
                  <p className="text-sm text-muted-foreground capitalize">{formattedDate} · 12:15 – 14:15</p>
                )}
                <div className="border-t border-border pt-3 flex justify-between items-center">
                  <span className="text-muted-foreground">{isFr ? 'Total' : 'Total'}</span>
                  <span className="text-2xl font-bold text-foreground">{WEBINAR_PRICE}</span>
                </div>
              </div>

              {/* Simulation payment */}
              <div className="p-3 rounded-lg bg-accent/20 border border-accent/30 text-center">
                <p className="text-xs font-medium text-accent-foreground">
                  {isFr ? '⚠️ Mode simulation — Aucun paiement réel ne sera effectué' : '⚠️ Simulation mode — No real payment will be processed'}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>{isFr ? 'Numéro de carte' : 'Card number'}</Label>
                  <Input placeholder="4242 4242 4242 4242" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{isFr ? 'Expiration' : 'Expiry'}</Label>
                    <Input placeholder="MM/AA" />
                  </div>
                  <div>
                    <Label>CVC</Label>
                    <Input placeholder="123" />
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep('slot')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />{isFr ? 'Retour' : 'Back'}
                </Button>
                <Button onClick={handlePayment} disabled={isSubmitting}>
                  {isSubmitting ? (isFr ? 'Traitement...' : 'Processing...') : (isFr ? `Payer ${WEBINAR_PRICE}` : `Pay ${WEBINAR_PRICE}`)}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <WaitlistDialog
        open={showWaitlist}
        onOpenChange={setShowWaitlist}
        trainingSlug={slug || ''}
        trainingTitle={training?.title || ''}
        initialEmail={user?.email || guestEmail || ''}
        selectedDate={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined}
      />
    </div>
  );
};
